import { useEffect, useId, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { CalendarClock, ChevronDown, History, Link2, Lock, ShieldCheck, Trash2, Upload, Users, Zap } from 'lucide-react';
import type { LibraryFile } from '@mailpilot/shared';
import { api, type BodyOf } from '../../lib/api';
import { qk } from '../../lib/keys';
import { uploadFile } from '../../lib/upload';
import { bytes, fileKind, shortDate, toLocalInput } from '../../lib/format';
import { Badge, Button, Card, ChipInput, IconButton, Input, Switch, spring, useConfirm, useToast } from '../ui';
import { fileIcon } from '../mail/AttachmentChip';

export type LibraryPatch = BodyOf<'PATCH /api/library/:id'>;

function dateValue(iso: string | null): string {
  return iso ? toLocalInput(iso).slice(0, 10) : '';
}
function dateToIso(v: string): string | null {
  if (!v) return null;
  const d = new Date(`${v}T23:59:59`);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

export function LibraryFileCard({ file, onPatch, onShare }: { file: LibraryFile; onPatch: (id: string, patch: LibraryPatch) => void; onShare: (file: LibraryFile) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const versionRef = useRef<HTMLInputElement>(null);
  const nameId = useId();
  const expiryId = useId();
  const permsId = useId();
  const [name, setName] = useState(file.friendlyName);
  const [open, setOpen] = useState(false);
  useEffect(() => setName(file.friendlyName), [file.friendlyName]);

  const superseded = !!file.supersededById;
  const expired = !!file.expiresAt && new Date(file.expiresAt).getTime() <= Date.now();
  const restricted = file.allowedRecipients.length + file.allowedDomains.length;

  const newVersion = useMutation({
    mutationFn: async (f: File) => {
      const up = await uploadFile(f);
      return api('POST /api/library/:id/version', { params: { id: file.id }, body: { uploadId: up.uploadId } });
    },
    onSuccess: (r) => {
      toast.success(`Version ${r.file.version} uploaded. Review its approval and permissions — they apply per version.`);
      void qc.invalidateQueries({ queryKey: qk.library });
    },
    onError: (e) => toast.error(e),
  });

  const remove = useMutation({
    mutationFn: () => api('DELETE /api/library/:id', { params: { id: file.id } }),
    onSuccess: () => {
      toast.success('File removed from the library');
      void qc.invalidateQueries({ queryKey: qk.library });
      void qc.invalidateQueries({ queryKey: qk.librarySuggestions });
    },
    onError: (e) => toast.error(e),
  });

  const saveName = () => {
    const n = name.trim();
    if (!n) {
      setName(file.friendlyName);
      return;
    }
    if (n !== file.friendlyName) onPatch(file.id, { friendlyName: n });
  };

  return (
    <Card className={clsx('group hover-lift', superseded && 'opacity-60')}>
      <div className="flex items-start gap-4 p-5">
        <span className={clsx('mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-[13px]', file.approved && !superseded && !expired ? 'bg-primary-soft text-primary-ink' : 'bg-raised text-muted')}>
          {fileIcon(file.mimeType, 'h-5 w-5')}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <label htmlFor={nameId} className="sr-only">
              Friendly name
            </label>
            <input
              id={nameId}
              value={name}
              disabled={superseded}
              onChange={(e) => setName(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur();
                if (e.key === 'Escape') {
                  setName(file.friendlyName);
                  e.currentTarget.blur();
                }
              }}
              title="Click to rename"
              className="-ml-1.5 min-w-0 max-w-full flex-1 rounded-lg border border-transparent bg-transparent px-1.5 py-0.5 text-[0.9375rem] font-semibold text-ink transition-colors hover:border-line focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25 disabled:hover:border-transparent"
            />
          </div>
          <p className="mt-0.5 truncate text-[0.8125rem] text-muted">
            {file.filename} · {fileKind(file.mimeType, file.filename)} · {bytes(file.size)} · v{file.version}
            {file.historicalUseCount > 0 && ` · sent ${file.historicalUseCount}× before`}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {superseded ? (
              <Badge tone="warn">Replaced by newer version</Badge>
            ) : expired ? (
              <Badge tone="danger">Expired</Badge>
            ) : file.approved ? (
              <Badge tone="success" icon={<ShieldCheck className="h-3 w-3" aria-hidden />}>
                Approved
              </Badge>
            ) : (
              <Badge tone="neutral">Not approved</Badge>
            )}
            {file.sensitive && (
              <Badge tone="warn" icon={<Lock className="h-3 w-3" aria-hidden />}>
                Sensitive
              </Badge>
            )}
            {file.autoSendAllowed && file.approved && !superseded && (
              <Badge tone="primary" icon={<Zap className="h-3 w-3" aria-hidden />}>
                Auto-send
              </Badge>
            )}
            {restricted > 0 && (
              <Badge icon={<Users className="h-3 w-3" aria-hidden />}>
                {restricted} allowed {restricted === 1 ? 'recipient' : 'recipients'}
              </Badge>
            )}
            {file.expiresAt && !expired && (
              <Badge icon={<CalendarClock className="h-3 w-3" aria-hidden />}>Expires {shortDate(file.expiresAt)}</Badge>
            )}
            <Badge icon={file.source === 'upload' ? <Upload className="h-3 w-3" aria-hidden /> : <History className="h-3 w-3" aria-hidden />}>
              {file.source === 'upload' ? 'Uploaded' : 'From sent history'}
            </Badge>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          <input
            ref={versionRef}
            type="file"
            className="sr-only"
            aria-label={`Upload new version of ${file.friendlyName}`}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) newVersion.mutate(f);
              e.target.value = '';
            }}
          />
          {!superseded && <IconButton label="Upload new version" icon={<Upload className="h-[18px] w-[18px]" />} disabled={newVersion.isPending} onClick={() => versionRef.current?.click()} />}
          <IconButton label="Share links" icon={<Link2 className="h-[18px] w-[18px]" />} onClick={() => onShare(file)} />
          <IconButton
            label={`Delete ${file.friendlyName}`}
            icon={<Trash2 className="h-[18px] w-[18px]" />}
            className="hover:!bg-danger-soft hover:!text-danger-ink"
            disabled={remove.isPending}
            onClick={async () => {
              const ok = await confirm({
                title: `Delete “${file.friendlyName}” v${file.version}?`,
                message: 'The file is removed from the library and its share links stop working. Responder rules that reference it will no longer be able to send it.',
                confirmLabel: 'Delete',
                danger: true,
              });
              if (ok) remove.mutate();
            }}
          />
        </div>
      </div>

      {!superseded && (
        <>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-controls={permsId}
            className="flex w-full items-center gap-2 border-t border-line/60 px-5 py-2.5 text-left text-[0.75rem] font-semibold text-muted transition-colors hover:bg-raised/60 hover:text-ink"
          >
            <ChevronDown className={clsx('h-4 w-4 transition-transform', open && 'rotate-180')} aria-hidden />
            Approval, permissions & expiry
            <span className="ml-auto font-normal text-subtle">{open ? 'Hide' : 'Edit'}</span>
          </button>
          <AnimatePresence initial={false}>
            {open && (
              <motion.div id={permsId} initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={spring} className="overflow-hidden">
                <div className="grid gap-6 border-t border-line/60 bg-raised/30 px-5 py-5 md:grid-cols-2">
                  <div className="space-y-4">
                    <Switch
                      checked={file.approved}
                      onChange={(v) => onPatch(file.id, { approved: v })}
                      label="Approved"
                      description="Only approved files can be offered in replies. Approval applies to this exact version."
                    />
                    <Switch
                      checked={file.sensitive}
                      onChange={(v) => onPatch(file.id, { sensitive: v })}
                      label={
                        <span className="inline-flex items-center gap-1.5">
                          <Lock className="h-3.5 w-3.5" aria-hidden /> Sensitive
                        </span>
                      }
                      description="You'll be warned before this file is used."
                    />
                    <Switch
                      checked={file.autoSendAllowed}
                      disabled={!file.approved}
                      onChange={(v) => onPatch(file.id, { autoSendAllowed: v })}
                      label={
                        <span className="inline-flex items-center gap-1.5">
                          <Zap className="h-3.5 w-3.5" aria-hidden /> Auto-send allowed
                        </span>
                      }
                      description={file.approved ? 'A matching responder rule may send this version automatically to allowed recipients.' : 'Approve the file first.'}
                    />
                  </div>
                  <div className="space-y-4">
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor={expiryId} className="text-[0.8125rem] font-medium text-ink">
                        Expiry date
                      </label>
                      <div className="flex items-center gap-2">
                        <Input id={expiryId} type="date" value={dateValue(file.expiresAt)} onChange={(e) => onPatch(file.id, { expiresAt: dateToIso(e.target.value) })} className="max-w-[12rem]" />
                        {file.expiresAt && (
                          <Button size="sm" variant="ghost" onClick={() => onPatch(file.id, { expiresAt: null })}>
                            Clear
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-muted">{file.expiresAt ? `Not offered after ${shortDate(file.expiresAt)}.` : 'No expiry.'}</p>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <span className="text-[0.8125rem] font-medium text-ink">Allowed recipients</span>
                      <ChipInput label={`Allowed recipients for ${file.friendlyName}`} value={file.allowedRecipients} onChange={(v) => onPatch(file.id, { allowedRecipients: v })} placeholder="name@example.com, press Enter" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <span className="text-[0.8125rem] font-medium text-ink">Allowed domains</span>
                      <ChipInput label={`Allowed domains for ${file.friendlyName}`} value={file.allowedDomains} onChange={(v) => onPatch(file.id, { allowedDomains: v.map((d) => d.replace(/^@/, '').toLowerCase()) })} placeholder="example.com, press Enter" />
                    </div>
                    <p className="text-xs leading-relaxed text-muted">
                      {restricted
                        ? 'Automatic sending is limited to these recipients and domains.'
                        : 'No recipient restrictions set. Add recipients or domains to control who this file may be sent to.'}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </Card>
  );
}
